import React, { createContext, useContext, useMemo, useState } from "react";
import { useRouter } from "next/router";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import AppButton from "@/components/buttons/AppButton/AppButton";
import WarningIcon from "@/components/icons/warningIcon";

type AuthDialogContextType = {
  handle401: () => void;
};

const AuthDialogContext = createContext<AuthDialogContextType>({
  handle401: () => {},
});

export const useAuthDialog = () => useContext(AuthDialogContext);

export const AuthDialogProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const router = useRouter();
  const [open, setOpen] = useState(false);

  const handle401 = () => {
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSigninAgain = () => {
    handleClose();
    document.cookie.split(";").forEach((cookie) => {
      const name = cookie.split("=")[0].trim();
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
    sessionStorage.clear();
    localStorage.clear();
    router.replace("/signin");
  };

  const value = useMemo(() => ({ handle401 }), [handle401]);

  return (
    <AuthDialogContext.Provider value={value}>
      {children}

      <Dialog
        open={open}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle className="flex items-center gap-2">
          <WarningIcon />
          {"Interaction required"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            The portal encountered an issue while attempting to retrieve access
            tokens. We suggest attempting to sign in again, or alternatively,
            continuing without access tokens, although this may result in a
            suboptimal user experience
          </DialogContentText>
        </DialogContent>
        <DialogActions className="mb-2 me-2">
          <AppButton
            onClick={handleSigninAgain}
            variant="primary"
            label="Sign in again"
          />
          <AppButton onClick={handleClose} variant="outlined" label="Ignore" />
        </DialogActions>
      </Dialog>
    </AuthDialogContext.Provider>
  );
};
